import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Stepper from "../../../components/profile/Stepper";
import ProfileSidebar from "../../../components/reusable components/ProfileSidebar";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import UploadDropzone from "../../../components/ui/UploadDropzone";
import Card from "./_Card";

import {
  AuditDocument,
  deleteFileDocumentUpload,
  FundingProfile,
  getProfileFunding,
  getUploadProfileDocument,
  putProfileFunding,
  uploadProfileDocument,
} from "../../../api/profile";
import { getBudgetRanges, getFundingSources, Option } from "../../../lib/api";
import { Plus, X } from "lucide-react";
import Input from "../../../components/ui/Input";
import { MultiSelect } from "../../../components/ui/MultiSelect";
import ChipMultiSelect from "../../../components/ui/ChipMultiSelect";
import { useAuth } from "../../../contexts/AuthContext";
import {
  FUNDING_AMOUNT_OPTIONS,
  FUNDING_TIMELINE_OPTIONS,
  FUNDING_TYPE_OPTIONS,
} from "../../../features/onboarding/constants";
import {
  resolveOnboardingRecord,
  updateOnboardingDraft,
} from "../../../features/onboarding/service";
import { valueExistsInOptions } from "./selectHelpers";

const ArrowRight = () => <span className="material-icons">arrow_right_alt</span>;

type FundingForm = {
  allowableExpenses: string[];
  annualRange: string;
  pastSource: string;
  goal: string;
  notes: string;
  auditUrl: string | null;
  min: string | null;
  max: string | null;
  fundingTypes: string[];
  preferredFunderTypes: string[];
  fundingStartDate: string | null;
  deadlinePreferences: string[];
  fixedDeadlineStart: string | null;
  fixedDeadlineEnd: string | null;
  taxStatus: string;
  yearsOperating: string;
  matchingFunds: string;
  applicationRequirements: string[];
  keywords: string;
  seekingInvestment: string;
  investmentAmount: string | null;
};

const toForm = (api: FundingProfile): FundingForm => {
  const annual =
    typeof api.funding_range === "string"
      ? api.funding_range
      : api.funding_range?.range ?? "";

  const source =
    Array.isArray(api.past_funding_sources) && api.past_funding_sources.length
      ? String(api.past_funding_sources[0])
      : "";

  const extraDetails = api.funding_extra_details || {};
  
  let notes = "";
  try {
    if (typeof extraDetails.notes === "string") {
      const parsed = JSON.parse(extraDetails.notes);
      notes = Array.isArray(parsed) && parsed.length > 0 ? JSON.stringify(parsed) : "";
    }
  } catch {
    notes = extraDetails.notes || "";
  }

  const windows = api.target_application_windows || {};

  return {
    annualRange: annual || "",
    pastSource: source || "",
    goal: api.funding_goal || "",
    notes: notes,
    auditUrl: api.audit_statement || null,
    min: api.min_funding_amount_required?.toString() || null,
    max: api.max_funding_aount_required?.toString() || null,
    allowableExpenses: Array.isArray(api.allowable_expenses) ? api.allowable_expenses : [],
    fundingTypes: Array.isArray(api.funding_types) ? api.funding_types : [],
    preferredFunderTypes: Array.isArray(api.perferred_funder_types) ? api.perferred_funder_types : [],
    fundingStartDate: api.funding_start_date || null,
    deadlinePreferences: Array.isArray(api.deadline_preferences) ? api.deadline_preferences : [],
    fixedDeadlineStart: windows.start || null,
    fixedDeadlineEnd: windows.end || null,
    taxStatus: extraDetails.tax_status || "",
    yearsOperating: extraDetails.years_operating || "",
    matchingFunds: extraDetails.matching_funds || "",
    applicationRequirements: Array.isArray(extraDetails.application_requirements) 
      ? extraDetails.application_requirements 
      : [],
    keywords: extraDetails.keywords || "",
    seekingInvestment: extraDetails.seeking_investment || "",
    investmentAmount: extraDetails.investment_amount || null,
  };
};

function DocumentPopup({
  show,
  selectedDocName,
  pendingFile,
  onClose,
  onSubmit,
  onFileChange,
}: {
  show: boolean;
  selectedDocName: string | null;
  pendingFile: File | null;
  onClose: () => void;
  onSubmit: (name: string, expiry: string) => void;
  onFileChange: (file: File) => void;
}) {
  const [name, setName] = React.useState("audit_statement");
  const [expiry, setExpiry] = React.useState("");

  const modalFileInput = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    setName(selectedDocName || "audit_statement");
    setExpiry("");
  }, [selectedDocName]);

  if (!show) return null;
  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-[400px] space-y-4">
        <h2 className="text-lg font-semibold">Upload Audit Document</h2>

        <div className="space-y-1">
          <label className="text-sm font-medium">Selected File</label>
          <div className="border px-3 py-2 w-full rounded bg-gray-50 min-w-0">
  <p
    className="truncate w-full text-sm"
    title={pendingFile?.name}
  >
    {pendingFile ? pendingFile.name : "No file selected"}
  </p>
</div>       <button
            className="text-blue-600 underline text-sm mt-1"
            onClick={() => modalFileInput.current?.click()}
          >
            Change File
          </button>

          <input
            ref={modalFileInput}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFileChange(file);
              e.target.value = "";
            }}
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Document Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border px-3 py-2 w-full rounded"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Expiry Date</label>
          <input
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            min={todayStr}
            className="border px-3 py-2 w-full rounded"
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(name, expiry)}>Submit</Button>
        </div>
      </div>
    </div>
  );
}

export default function Funding() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [form, setForm] = React.useState<FundingForm>({
    annualRange: "",
    pastSource: "",
    goal: "",
    notes: "",
    auditUrl: null,
    min: null,
    max: null,
    allowableExpenses: [],
    fundingTypes: [],
    preferredFunderTypes: [],
    fundingStartDate: null,
    deadlinePreferences: [],
    fixedDeadlineStart: null,
    fixedDeadlineEnd: null,
    taxStatus: "",
    yearsOperating: "",
    matchingFunds: "",
    applicationRequirements: [],
    keywords: "",
    seekingInvestment: "",
    investmentAmount: null,
  });

  const [rangeOpts, setRangeOpts] = React.useState<Option[]>([]);
  const [sourceOpts, setSourceOpts] = React.useState<Option[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [matchFundingTypes, setMatchFundingTypes] = React.useState<string[]>([]);
  const [matchFundingAmountBand, setMatchFundingAmountBand] = React.useState("");
  const [matchFundingTimeline, setMatchFundingTimeline] = React.useState("");

  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [showDocPopup, setShowDocPopup] = React.useState(false);
  const [selectedDocName, setSelectedDocName] = useState<string | null>(null);

  const [auditDocuments, setAuditDocuments] = useState<AuditDocument[]>([]);
  const [fileDeleted, setFileDeleted] = useState(false);
  const userKey = user?.uid || user?.email || "anonymous";

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const staticIncludes = (value: string, options: string[]) =>
    !!value && options.includes(value);
  const safeAnnualRange = valueExistsInOptions(form.annualRange || "", rangeOpts)
    ? form.annualRange
    : "";
  const safePastSource = valueExistsInOptions(form.pastSource || "", sourceOpts)
    ? form.pastSource
    : "";
  const safeFundingAmountBand = staticIncludes(
    matchFundingAmountBand,
    FUNDING_AMOUNT_OPTIONS.map((o) => o.value)
  )
    ? matchFundingAmountBand
    : "";
  const safeFundingTimeline = staticIncludes(
    matchFundingTimeline,
    FUNDING_TIMELINE_OPTIONS.map((o) => o.value)
  )
    ? matchFundingTimeline
    : "";
  const safeTaxStatus = staticIncludes(form.taxStatus, [
    "Registered charity",
    "CIC",
    "501(c)(3)",
    "For-profit LTD",
    "Sole trader",
    "Unregistered group",
    "University / academic",
    "Other",
  ])
    ? form.taxStatus
    : "";
  const safeYearsOperating = staticIncludes(form.yearsOperating, ["0-1", "1-3", "3-5", "5+"])
    ? form.yearsOperating
    : "";
  const safeMatchingFunds = staticIncludes(form.matchingFunds, ["Yes", "No", "Depends on amount"])
    ? form.matchingFunds
    : "";
  const safeSeekingInvestment = staticIncludes(form.seekingInvestment, ["Yes", "No"])
    ? form.seekingInvestment
    : "";

  const openFilePicker = (docName: string) => {
    setSelectedDocName(docName);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  React.useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        const [metaRanges, metaSources, funding] = await Promise.all([
          getBudgetRanges(),
          getFundingSources(),
          getProfileFunding(),
        ]);

        if (!active) return;

        setRangeOpts(metaRanges);
        setSourceOpts(metaSources);

        const uploadedDocs = await getUploadProfileDocument();
        setAuditDocuments(uploadedDocs);

        const seed = toForm(funding);
        setForm((s) => ({
          ...s,
          ...seed,
          // Keep existing values, but never auto-pick first option from API listings.
          annualRange: (s.annualRange || seed.annualRange) && valueExistsInOptions(s.annualRange || seed.annualRange || "", metaRanges)
            ? (s.annualRange || seed.annualRange || "")
            : "",
          pastSource: (s.pastSource || seed.pastSource) && valueExistsInOptions(s.pastSource || seed.pastSource || "", metaSources)
            ? (s.pastSource || seed.pastSource || "")
            : "",
        }));
        setFileDeleted(false);
      } catch (e: any) {
        if (active) setError(e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| "Failed to load funding");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [showDocPopup, fileDeleted]);

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const record = await resolveOnboardingRecord(userKey);
        if (!active) return;
        setMatchFundingTypes(record.data.fundingTypes);
        setMatchFundingAmountBand(record.data.fundingAmountBand);
        setMatchFundingTimeline(record.data.fundingTimeline);
      } catch {
      }
    })();
    return () => {
      active = false;
    };
  }, [userKey]);

  const buildPayload = (): any => {
    const funding_range = form.annualRange ? { range: form.annualRange } : {};
    const past_funding_sources = form.pastSource ? [form.pastSource] : [];

    return {
      funding_goal: form.goal,
      funding_range,
      previous_funding: [],
      past_funding_sources,
      audit_statement: form.auditUrl,
      funding_types: form.fundingTypes,
      allowable_expenses: form.allowableExpenses,
      perferred_funder_types: form.preferredFunderTypes,
      funding_start_date: form.fundingStartDate,
      deadline_preferences: form.deadlinePreferences,
      target_application_windows: {
        start: form.fixedDeadlineStart,
        end: form.fixedDeadlineEnd,
      },
      min_funding_amount_required: form.min ? parseFloat(form.min) : 0,
      max_funding_aount_required: form.max ? parseFloat(form.max) : null,
      funding_extra_details: {
        notes: form.notes,
        tax_status: form.taxStatus,
        years_operating: form.yearsOperating,
        matching_funds: form.matchingFunds,
        application_requirements: form.applicationRequirements,
        keywords: form.keywords,
        seeking_investment: form.seekingInvestment,
        investment_amount: form.investmentAmount,
        onboarding_funding_types: matchFundingTypes,
        onboarding_funding_amount_band: matchFundingAmountBand,
        funding_timeline: matchFundingTimeline,
      },
    };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await putProfileFunding(buildPayload());
      updateOnboardingDraft(userKey, {
        fundingTypes: matchFundingTypes,
        fundingAmountBand: matchFundingAmountBand,
        fundingTimeline: matchFundingTimeline,
      });
      toast.success("Funding saved");
    } catch (e: any) {
      toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail|| "Failed to save funding");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = (file: File) => {
    setPendingFile(file);

    if (!selectedDocName) {
      setSelectedDocName("audit_statement");
    }

    setShowDocPopup(true);
  };

  const handleDocumentSubmit = async (name: string, expiry: string) => {
    if (!pendingFile) return;

    if (!expiry) {
      toast.error("Please select an expiry date");
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expDate = new Date(expiry);
    if (expDate < today) {
      toast.error("Expiry date cannot be in the past");
      return;
    }

    try {
      setUploading(true);
      await uploadProfileDocument(pendingFile, name, expiry);

      toast.success("Document uploaded");

      const updatedDocs = await getUploadProfileDocument();
      setAuditDocuments(updatedDocs);

      setShowDocPopup(false);
      setPendingFile(null);
      setSelectedDocName(null);
    } catch (e) {
      toast.error("Upload failed, Please Upload again..");
      setShowDocPopup(false);
      setPendingFile(null);
      setSelectedDocName(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFileUpload = async (id: number | null) => {
    await deleteFileDocumentUpload(id);
    setFileDeleted(true);
    toast.success("file deleted successfully.");
  };

  const goNext = async () => {
    await handleSave();
    navigate("/dashboard/profile/social");
  };

  const isPdfFile = (url?: string | null) => {
    if (!url) return false;
    const clean = url.split("?")[0].split("#")[0];
    return clean.toLowerCase().endsWith(".pdf");
  };

  const handleCheckboxChange = (field: keyof FundingForm, value: string) => {
    setForm((prev) => {
      const currentArray = prev[field] as string[];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item) => item !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  return (
    <div className="flex min-h-screen gap-5 px-3">
      <ProfileSidebar isProfileRoute={true} />

      <div className="flex-1 min-w-0 overflow-y-auto">
        <Card>
          <div className="px-6 py-5">
            <Stepper active="funding" />
          </div>

          <div className="px-6 pb-6 space-y-6">
            <div className="text-[20px] font-semibold">Funding</div>

            <div className="rounded-2xl border border-[#E5E7EB] bg-[#F8FAFC] p-4">
              <div className="text-[18px] font-semibold text-[#111827]">
                Onboarding funding preferences
              </div>
              <div className="mt-1 text-sm text-[#6B7280]">
                Edit the funding choices collected during first-login onboarding.
              </div>

              <div className="mt-4 grid gap-4">
                <div>
                  <div className="text-sm font-semibold mb-1">Funding types</div>
                  <ChipMultiSelect
                    options={FUNDING_TYPE_OPTIONS.map((option) => option.value)}
                    value={matchFundingTypes}
                    onChange={setMatchFundingTypes}
                    placeholder="Choose funding types"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-sm font-semibold mb-1">Funding amount band</div>
                    <Select
                      value={safeFundingAmountBand}
                      onChange={(e) => setMatchFundingAmountBand(e.target.value)}
                    >
                      <option value="" disabled>Select funding amount band</option>
                      {FUNDING_AMOUNT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">Funding timeline</div>
                    <Select
                      value={safeFundingTimeline}
                      onChange={(e) => setMatchFundingTimeline(e.target.value)}
                    >
                      <option value="" disabled>Select funding timeline</option>
                      {FUNDING_TIMELINE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 gap-x-20">
              <div>
                <div className="text-sm font-semibold mb-1">
                  Annual Budget / Revenue Range
                </div>
                <Select
                  value={safeAnnualRange}
                  onChange={(e) =>
                    setForm({ ...form, annualRange: e.target.value })
                  }
                >
                  <option value="" disabled>Select annual budget range</option>
                  {rangeOpts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                {form.annualRange === "Other" && (
                  <div>
                    <div className="text-sm font-semibold mb-1 mt-6">
                      Amount Needed:
                    </div>
                    <div className="flex gap-2 w-full justify-between mt-1">
                      <div className="w-full">
                        <div className="text-sm font-semibold mb-1">
                          Minimum Amount Needed:
                        </div>
                        <Input
                          value={form.min ?? ""}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, min: e.target.value }))
                          }
                        />
                      </div>
                      <div className="w-full">
                        <div className="text-sm font-semibold mb-1">
                          Maximum Amount Needed:
                        </div>
                        <Input
                          value={form.max ?? ""}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, max: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">
                  Past Funding Sources
                </div>
                <Select
                  value={safePastSource}
                  onChange={(e) =>
                    setForm({ ...form, pastSource: e.target.value })
                  }
                >
                  <option value="" disabled>Select past funding source</option>
                  {sourceOpts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                    <div className="flex gap-2 w-full justify-between mt-1">
                      <div className="w-full">
                        <div className="text-sm font-semibold mb-1">
                          Min Amount Needed:
                        </div>
                        <Input
                          value={form.min ?? ""}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, min: e.target.value }))
                          }
                        />
                      </div>
                      <div className="w-full">
                        <div className="text-sm font-semibold mb-1">
                          Max Amount Needed:
                        </div>
                        <Input
                          value={form.max ?? ""}
                          onChange={(e) =>
                            setForm((s) => ({ ...s, max: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">Funding Type</div>
                <MultiSelect
                  options={[
                    "Grant (non-repayable)",
                    "Fellowship",
                    "Scholarship",
                    "Award / prize",
                    "Subsidy / rebate",
                  ]}
                  value={form.fundingTypes}
                  onChange={(value) => setForm({ ...form, fundingTypes: value })}
                  placeholder="Select funding types..."
                />
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">
                  Allowable Expenses Needed
                </div>
                <MultiSelect
                  options={[
                    "Staff salaries",
                    "Contractors / freelancers",
                    "Equipment",
                    "Software / tools",
                    "Travel",
                    "Operating costs",
                    "Capital improvements",
                    "Training",
                    "Indirect / overhead",
                  ]}
                  value={form.allowableExpenses}
                  onChange={(value) =>
                    setForm({ ...form, allowableExpenses: value })
                  }
                  placeholder="Select allowable expenses..."
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold mb-1">
                  When do you want funding to start?
                </label>
                <input
                  type="date"
                  value={form.fundingStartDate || ""}
                  onChange={(e) =>
                    setForm({ ...form, fundingStartDate: e.target.value })
                  }
                  className="border px-3 py-2 w-full rounded"
                />
              </div>

              <div>
                <div className="text-sm font-semibold mb-1">
                  Deadlines you prefer
                </div>
                <MultiSelect
  options={["Rolling deadlines", "Fixed deadlines", "Either"]}
  value={form.deadlinePreferences}
  onChange={(value) => {
  const previous = form.deadlinePreferences;

  const hasEither = value.includes("Either");
  const hadEither = previous.includes("Either");

  const hasRollingOrFixed =
    value.includes("Rolling deadlines") ||
    value.includes("Fixed deadlines");

  let updated = value;

  // Case 1: User clicks "Either"
  if (hasEither && !hadEither) {
    updated = ["Either"];
  }

  // Case 2: User selects Rolling/Fixed while Either was selected
  if (hadEither && hasRollingOrFixed) {
    updated = value.filter((v) => v !== "Either");
  }

  setForm({ ...form, deadlinePreferences: updated });
}}
/>
                
              </div>

              <div className="md:col-span-2">
                <div className="text-sm font-semibold mb-4">
                  If fixed deadlines: filter range
                </div>
                <div className="grid md:grid-cols-2 gap-6 gap-x-20">
                  <div className="space-y-1">
                    <div className="text-sm font-semibold mb-1">Start Date</div>
                    <input
                      type="date"
                      value={form.fixedDeadlineStart || ""}
                      onChange={(e) =>
                        setForm({ ...form, fixedDeadlineStart: e.target.value })
                      }
                      className="border px-3 py-2 w-full rounded"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm font-semibold mb-1">End Date</div>
                    <input
                      type="date"
                      value={form.fixedDeadlineEnd || ""}
                      onChange={(e) =>
                        setForm({ ...form, fixedDeadlineEnd: e.target.value })
                      }
                      className="border px-3 py-2 w-full rounded"
                    />
                  </div>
                </div>
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">
                  Tax Status / Registration
                </div>
                <Select
                  value={safeTaxStatus}
                  onChange={(e) =>
                    setForm({ ...form, taxStatus: e.target.value })
                  }
                >
                  <option value="" disabled>Select</option>
                  {[
                    "Registered charity",
                    "CIC",
                    "501(c)(3)",
                    "For-profit LTD",
                    "Sole trader",
                    "Unregistered group",
                    "University / academic",
                    "Other",
                  ].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">
                  Years in Operation
                </div>

                <Select
                  value={safeYearsOperating}
                  onChange={(value) => setForm({ ...form, yearsOperating:value.target.value})}
                >
                  <option value="" disabled>Select</option>
                  {["0-1","1-3","3-5","5+"].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">
                  Matching Funds Requirement
                </div>
                <Select
                  value={safeMatchingFunds}
                  onChange={(e) =>
                    setForm({ ...form, matchingFunds: e.target.value })
                  }
                >
                  <option value="" disabled>Select</option>
                  {["Yes", "No", "Depends on amount"].map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">
                  Application Requirements
                </div>
                <MultiSelect
                  options={[
                    "Full proposal",
                    "Short form application",
                    "Budget spreadsheet",
                    "Budget narrative",
                    "Letters of support",
                    "Financial statements",
                    "IRS / HMRC documentation",
                    "Evaluation plan",
                    "Reporting (monthly)",
                    "Reporting (quarterly)",
                    "Reporting (annual)",
                  ]}
                  value={form.applicationRequirements}
                  onChange={(value) => setForm({ ...form, applicationRequirements: value })}
                  placeholder="Select application requirements..."
                />
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">
                  Preferred Funder Types
                </div>
                <MultiSelect
                  options={[
                    "Federal / national government",
                    "State / local government",
                    "Private foundations",
                    "Corporate funders",
                    "Academic institutions",
                    "International organisations",
                  ]}
                  value={form.preferredFunderTypes}
                  onChange={(value) => setForm({ ...form, preferredFunderTypes: value })}
                  placeholder="Select preferred funder types..."
                />
              </div>

              <div className="">
                <div className="text-sm font-semibold mb-1">Keywords</div>
                <Input
                  value={form.keywords}
                  placeholder="Add keywords that describe your work — e.g., STEM education for girls, affordable housing, postdoctoral research."
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                />
              </div>

              <div>
                <div className="text-sm font-semibold mb-1">
                  Seeking Investment
                </div>
                <Select
                  value={safeSeekingInvestment}
                  onChange={(e) =>
                    setForm({ ...form, seekingInvestment: e.target.value })
                  }
                >
                  <option value="" disabled>Select investment preference</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Select>
              </div>

              {form.seekingInvestment === "Yes" && (
                <div>
                  <div className="text-sm font-semibold mb-1">
                    Investment Amount
                  </div>
                  <Input
                    type="number"
                    value={form.investmentAmount ?? ""}
                    placeholder="Enter investment amount"
                    onChange={(e) =>
                      setForm({ ...form, investmentAmount: e.target.value })
                    }
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <div className="text-sm font-semibold mb-2">
                  Upload Documents
                </div>

                <div className="flex items-center gap-4">
                  <UploadDropzone onFile={onUpload} uploading={uploading} />
                </div>

                <div className="flex flex-wrap gap-6 mt-6">
                  {auditDocuments.map((group) => (
                    <div key={group.id}>
                      <div className="font-semibold text-sm mb-2">
                        <b>File name:</b> {group.name} ({group.files.length})
                      </div>

                      <div className="flex flex-wrap gap-4">
                        {group.files.map((f) => {
                          const pdf = isPdfFile(f.file);
                          return (
                            <div key={f.id} className="relative w-24 h-24 group">
                              <a href={f.file} target="_blank" rel="noreferrer">
                                {pdf ? (
                                  <div className="w-24 h-24 rounded-lg border bg-red-50 flex items-center justify-center">
                                    <span className="material-icons text-red-600 text-4xl">
                                      picture_as_pdf
                                    </span>
                                  </div>
                                ) : (
                                  <img
                                    src={f.file}
                                    className="w-24 h-24 object-cover rounded-lg border"
                                  />
                                )}
                              </a>

                              <button
                                className="absolute top-0 right-0 bg-white hidden group-hover:block"
                                onClick={() => handleDeleteFileUpload(f.id)}
                              >
                                <X />
                              </button>
                            </div>
                          );
                        })}

                        <div
                          className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-lg border cursor-pointer hover:bg-gray-200"
                          onClick={() => openFilePicker(group.name)}
                        >
                          <Plus size={28} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>

              <Button onClick={goNext}>
                Next <ArrowRight />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            onUpload(file);
          }
          e.target.value = "";
        }}
      />

      <DocumentPopup
        show={showDocPopup}
        selectedDocName={selectedDocName}
        pendingFile={pendingFile}
        onClose={() => {
          setShowDocPopup(false);
          setSelectedDocName(null);
        }}
        onSubmit={handleDocumentSubmit}
        onFileChange={(file) => setPendingFile(file)}
      />
    </div>
  );
}
